import React, { FC, useEffect, useState } from 'react'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { RootState } from '../../../../states/containers/store'
import {
  fetchTaskDisplayCreateManualTaskSetting,
  fetchTaskThresholdSetting,
  updateTaskDisplayCreateManualTaskSetting,
  updateTaskThresholdSetting,
  fetchTaskLearningScopeSetting,
  updateTaskLearningScopeSetting,
} from '../../../../states/containers/task/taskSlice'
import LoadingSquare from '../../../components/common/LoadingSquare'
import { setPath } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { URL } from '../../../constants/url'
import { isAdministrator } from '../../../../utils/role'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

type Scope = 'member' | 'team' | null;

const Index: FC = () => {
  const {
    login,
    task,
  } = useSelector((state: RootState) => state)

  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const history = useHistory()
  const { language } = useParams<{ language: LangValue }>()

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [thresholdMinutes, setThresholdMinutes] = useState<number>(1)
  const [learningScope, setLearningScope] = useState<Scope>(null)
  const learningScopeOptions = [
    {
      label: "個人",
      value: "member",
    },
    {
      label: "チーム",
      value: "team",
    },
    {
      label: "会社",
      value: null,
    },
  ]

  const [isDisplayCreateManualTaskZone, setIsDisplayCreateManualTaskZone] = useState<boolean>(false)

  const modThreshold = () => {
    if (thresholdMinutes < 1 || thresholdMinutes > 10) return
    dispatch(
      updateTaskThresholdSetting(
        {
          task_threshold: thresholdMinutes * 60,
        },
        () => {
          alert.success(`仕分け画面に表示する作業データの最小時間を${thresholdMinutes}分に変更しました。`)
        },
      ),
    )
  }

  const modDisplayCreateManualTaskZone = () => {
    dispatch(
      updateTaskDisplayCreateManualTaskSetting(
        {
          display: isDisplayCreateManualTaskZone,
        },
        () => {
          alert.success(`仕分け画面の作業データ手動登録可能時間の表示を変更しました。`)
        },
      ),
    )
  }

  const modLearningScope = () => {
    dispatch(
      updateTaskLearningScopeSetting(
        {
          learning_scope: learningScope,
        },
        () => {
          alert.success(`学習範囲を${learningScopeOptions.find((v) => v.value === learningScope)?.label}に変更しました`)
        },
      ),
    )
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    if (!login.accessToken) return
    if (!isAdministrator(login.role)) {
      setIsLoading(false)
      return
    }
    dispatch(fetchTaskThresholdSetting())
    dispatch(fetchTaskLearningScopeSetting())
    dispatch(fetchTaskDisplayCreateManualTaskSetting())
  }, [login.accessToken, login.role])

  useEffect(() => {
    if (!task.taskThresholdSetting?.task_threshold) return
    setThresholdMinutes(Math.ceil(task.taskThresholdSetting.task_threshold / 60))
  }, [task.taskThresholdSetting?.task_threshold])

  useEffect(() => {
    if (!task.taskLearningScopeSetting?.learning_scope) return
    setLearningScope(task.taskLearningScopeSetting.learning_scope)
  }, [task.taskLearningScopeSetting?.learning_scope])

  useEffect(() => {
    setIsDisplayCreateManualTaskZone(task.taskDisplayCreateManualTaskSetting?.display || false)
  }, [task.taskDisplayCreateManualTaskSetting?.display])

  useEffect(() => {
    if (task.isLoading) return
    setIsLoading(false)
  }, [task.isLoading])

  return (
    <section className="task_page">
      <Helmet>
        <title>Qasee - 工数管理 - 設定 - その他の設定</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        設定
        &nbsp;&gt;&nbsp;
        その他の設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          その他の設定
        </h1>
      </section>
      <section className="page_description">
        <p>
          工数管理機能に関する詳細な設定ができます。
        </p>
      </section>
      {
        isLoading ? (
          <LoadingSquare>
            <>
              設定を読み込んでいます…
            </>
          </LoadingSquare>
        ) : (
          <>
            {
              isAdministrator(login.role) && (
                <>
                  <section className="page_contents content-area">
                    <div className="content-block">
                      <div className="block-header" style={{ display: "block" }}>
                        <div className="title" style={{ marginBottom: "16px" }}>
                          仕分け画面に表示する作業データの最小時間
                        </div>
                        <div className="description" style={{ fontSize: "12px" }}>
                          <p style={{ marginBottom: "4px" }}>仕分け画面に表示する作業データの最小時間を分単位で設定します。</p>
                          <p style={{ marginBottom: "4px" }}>最小時間が大きいほど作業データの表示数は減り仕分けしやすくなりますが、最小時間以下の作業データを仕分けることができなくなり精度が減少する可能性があります。</p>
                          <p style={{ marginBottom: "4px" }}>
                            詳しい設定方法は
                            <a href="https://qasee.zendesk.com/hc/ja/articles/43670943771289" target="_blank" rel="noreferrer">ヘルプページ</a>
                            をご確認いただくか、カスタマーサクセス窓口までご連絡ください。
                          </p>
                        </div>
                      </div>
                      <div
                        className="block-detail"
                        style={{
                          border: "solid 1px #CCC",
                          borderRadius: "4px",
                          padding: "16px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            marginBottom: "16px",
                          }}
                        >
                          仕分け画面に表示する作業データの最小時間を
                          <select
                            onChange={(e) => setThresholdMinutes(Number(e.target.value))}
                            value={thresholdMinutes}
                            defaultValue={thresholdMinutes}
                            style={{
                              border: "solid 1px #999",
                              backgroundColor: "#FFF",
                              borderRadius: "3px",
                              padding: "6px 8px",
                              appearance: "listbox",
                              WebkitAppearance: "listbox",
                            }}
                          >
                            {
                              Array(10).fill(null).map((_v, i) => (
                                <option value={i + 1} key={`option_${i + 1}`}>
                                  {i + 1}
                                </option>
                              ))
                            }
                          </select>
                          分に設定します (初期設定: 3分)
                        </div>
                        <button
                          type="button"
                          className="button"
                          onClick={modThreshold}
                        >
                          設定
                        </button>
                      </div>
                    </div>
                  </section>
                  <section className="page_contents content-area">
                    <div className="content-block">
                      <div className="block-header" style={{ display: "block" }}>
                        <div className="title" style={{ marginBottom: "16px" }}>
                          仕分け画面の作業データ手動登録可能時間を本人以外も表示
                        </div>
                        <div className="description" style={{ fontSize: "12px" }}>
                          <p style={{ marginBottom: "4px" }}>仕分け画面の作業データ手動登録可能時間を本人以外も表示できるようにします。</p>
                        </div>
                      </div>
                      <div
                        className="block-detail"
                        style={{
                          border: "solid 1px #CCC",
                          borderRadius: "4px",
                          padding: "16px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            marginBottom: "16px",
                            cursor: "pointer",
                            color: isDisplayCreateManualTaskZone ? "#008800" : "#333333",
                            userSelect: "none",
                          }}
                          onClick={() => setIsDisplayCreateManualTaskZone(!isDisplayCreateManualTaskZone)}
                          role="none"
                        >
                          <FontAwesomeIcon icon={['far', isDisplayCreateManualTaskZone ? 'check-square' : 'square']} style={{ fontSize: "18px" }} />
                          仕分け画面の作業データ手動登録可能時間を本人以外も表示する
                        </div>
                        <button
                          type="button"
                          className="button"
                          onClick={modDisplayCreateManualTaskZone}
                        >
                          設定
                        </button>
                      </div>
                    </div>
                  </section>
                  <section className="page_contents content-area">
                    <div className="content-block">
                      <div className="block-header" style={{ display: "block" }}>
                        <div className="title" style={{ marginBottom: "16px" }}>
                          作業データを自動生成しないメンバーの設定
                        </div>
                        <div className="description" style={{ fontSize: "12px" }}>
                          <p style={{ marginBottom: "4px" }}>作業データを自動生成しないメンバーを設定します。</p>
                          <p style={{ marginBottom: "4px" }}>メンバーに設定されていると、自動的に作業データが作成されません。ご注意ください。</p>
                        </div>
                      </div>
                      <div className="block-detail">
                        <button
                          type="button"
                          className="button"
                          onClick={() => history.push(`${setPath(URL.TASK_EXCLUDE_GENERATE_MEMBER_SETTING.path, language)}`)}
                        >
                          作業データを自動生成しないメンバーの設定を開く
                        </button>
                      </div>
                    </div>
                  </section>
                  <section className="page_contents content-area">
                    <div className="content-block">
                      <div className="block-header" style={{ display: "block" }}>
                        <div className="title" style={{ marginBottom: "16px" }}>
                          仕分け提案に使用する過去データの範囲設定
                        </div>
                        <div className="description" style={{ fontSize: "12px" }}>
                          <p style={{ marginBottom: "4px" }}>作業データの仕分け提案に使用する過去データの範囲を選択できます。</p>
                          <p style={{ marginBottom: "4px" }}>範囲の選択によって、他のメンバーの仕分け履歴を参考にするか、自分の履歴のみを使うかを調整できます。</p>
                        </div>
                      </div>
                      <div
                        className="block-detail"
                        style={{
                          border: "solid 1px #CCC",
                          borderRadius: "4px",
                          padding: "16px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            marginBottom: "16px",
                          }}
                        >
                          学習する範囲を
                          <select
                            onChange={(e) => setLearningScope(e.target.value === "null" ? null : (e.target.value as Scope))}
                            value={learningScope ?? "null"}
                            defaultValue={learningScope ?? "null"}
                            style={{
                              border: "solid 1px #999",
                              backgroundColor: "#FFF",
                              borderRadius: "3px",
                              padding: "6px 8px",
                              appearance: "listbox",
                              WebkitAppearance: "listbox",
                            }}
                          >
                            {
                              learningScopeOptions.map((v, i) => (
                                <option value={v.value ?? "null"} key={`option_${i + 1}`}>
                                  { v.label }
                                </option>
                              ))
                            }
                          </select>
                          に設定します (初期設定: 会社)
                        </div>
                        <button
                          type="button"
                          className="button"
                          onClick={modLearningScope}
                        >
                          設定
                        </button>
                      </div>
                    </div>
                  </section>
                </>
              )
            }
            {
              login.permissions?.task_project_setting && (
                <>
                  <section className="page_contents content-area">
                    <div className="content-block">
                      <div className="block-header" style={{ display: "block" }}>
                        <div className="title" style={{ marginBottom: "16px" }}>
                          ユーザーテンプレート設定
                        </div>
                        <div className="description" style={{ fontSize: "12px" }}>
                          <p style={{ marginBottom: "4px" }}>プロジェクト作成の際に使用できるテンプレートの作成、編集、削除ができます。</p>
                        </div>
                      </div>
                      <div className="block-detail">
                        <button
                          type="button"
                          className="button"
                          onClick={() => history.push(`${setPath(URL.TASK_PROJECT_TEMPLATE_SETTING.path, language)}`)}
                        >
                          ユーザーテンプレート設定を開く
                        </button>
                      </div>
                    </div>
                  </section>
                </>
              )
            }
          </>
        )
      }
    </section>
  )
}

export default Index
